import { QueryClient, type DefaultOptions } from '@tanstack/react-query';
import { ApiError } from '@/api/client/api.error';

/**
 * Single TanStack Query client for the whole app. Constructed once and
 * handed to the QueryClientProvider in `app/providers/query-provider.tsx`.
 *
 * Defaults rationale:
 *   - **staleTime 0** — caching is disabled: data is considered stale the
 *     moment it arrives, so every mount/focus refetches fresh from the API.
 *   - **gcTime 0** — don't retain query data after it's no longer in use;
 *     nothing is served from cache between navigations.
 *   - **refetchOnMount 'always'** — re-run the query on every mount even if
 *     an entry somehow lingers, guaranteeing a network hit.
 *   - **No retry on 4xx** — client errors won't fix themselves on retry,
 *     so don't waste time. 5xx and network errors retry twice.
 *   - **refetchOnWindowFocus: true** — matches the user expectation that
 *     "this is the data I see in another tab".
 *   - **Mutations don't retry** — at-least-once writes risk duplicate side
 *     effects. Callers decide explicitly when retry is safe.
 */
const defaultOptions: DefaultOptions = {
    queries: {
        staleTime: 0,
        gcTime:    0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: true,
        retry: (failureCount, err) => {
            if (err instanceof ApiError) {
                if (err.status >= 400 && err.status < 500) return false;
                if (err.isNetwork) return failureCount < 2;
            }
            return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8_000),
    },
    mutations: {
        retry: false,
    },
};

export const queryClient = new QueryClient({ defaultOptions });
